import type { OverrideEditor, PluginConfig } from 'platejs'

import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'
import { isNextCharacterText } from '~publish/legacy/editor/queries/isNextCharacterText'
import { isPreviousCharacterWhitespaceOrStart } from '~publish/legacy/editor/queries/isPreviousCharacterWhitespaceOrStart'

import type {
  AutocompleteInputElement,
  AutocompleteInputElementInterface,
} from './nodes'
import type { AutocompletePluginOptions } from './types'

/**
 * `withTriggerCombobox` only checks the character *before* the trigger, so
 * typing it directly before existing text (no whitespace, e.g. "@kai" typed
 * as one run) still opens an empty combobox — the trailing text is left
 * stranded as separate plain text instead of becoming the initial search
 * query. Chained after `withTriggerCombobox` on the same plugin, this
 * intercepts that one case and delegates everything else (the normal
 * trigger case `withTriggerCombobox` already handles, and all non-trigger
 * text) to the transform it wraps.
 */
export const withInsertTriggerBeforeText =
  <E extends AutocompleteInputElement>(
    elementInterface: AutocompleteInputElementInterface<E>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches withTriggerCombobox's own generic shape (PluginConfig<any, ...>).
  ): OverrideEditor<PluginConfig<any, AutocompletePluginOptions<E>>> =>
  ({ editor, getOptions, tf: { insertText } }) => ({
    transforms: {
      insertText(text, options): void {
        const { onInsertTriggerBeforeText, triggerQuery } = getOptions()
        const typedEditor = editor as BufferEditor

        if (
          options?.at ||
          !typedEditor.selection ||
          text !== elementInterface.trigger ||
          !onInsertTriggerBeforeText ||
          (triggerQuery && !triggerQuery(typedEditor))
        ) {
          insertText(text, options)
          return
        }

        const pointBeforeIsValid =
          isPreviousCharacterWhitespaceOrStart(typedEditor)
        const pointAfterIsText = isNextCharacterText(typedEditor)

        if (!pointBeforeIsValid || !pointAfterIsText) {
          insertText(text, options)
          return
        }

        onInsertTriggerBeforeText(typedEditor, elementInterface)
      },
    },
  })
